// --- START OF FILE WorkloadManagement.js ---

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Calendar, AlertCircle } from 'lucide-react';
import { useAuth } from './Auth';
import { getWorkingDays, getWorkingDaysDates } from '../utils/dateUtils';
import { apiService } from '../services/api';

const apiBaseUrl =
  process.env.NODE_ENV === 'development'
    ? `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}`
    : `${process.env.REACT_APP_API_URL}/api` || 'http://localhost:8000';

const WorkloadManagement = ({ slot, sprints, employees }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [velocity, setVelocity] = useState(10);
  const [unavailabilities, setUnavailabilities] = useState({});
  const [needsSave, setNeedsSave] = useState(false);
  const [objectives, setObjectives] = useState([]);
  const [showProgress, setShowProgress] = useState(false);

  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    if (sprints.length > 0) {
      const projectIds = Array.from(new Set(sprints.map(sprint => sprint.project_id)));
      Promise.all(
        projectIds.map(projectId => apiService.get(`/projects/${projectId}/objectives`))
      )
        .then(results => {
          const allObjectives = results.flat();
          setObjectives(allObjectives);
        })
        .catch(console.error);
    }
  }, [sprints]);

  useEffect(() => {
    const fetchWorkloadData = async () => {
      if (!slot?.id) return;
      try {
        const response = await fetch(`${apiBaseUrl}/slots/${slot.id}/workload`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.detail || 'Échec du chargement des données de charge de travail'
          );
        }
        const data = await response.json();
        setVelocity(data.velocity || 10);
        if (Array.isArray(data.employeeUnavailabilities)) {
          const newUnavailabilities = {};
          data.employeeUnavailabilities.forEach((item) => {
            newUnavailabilities[item.employee_id] = Number(item.unavailable_days);
          });
          setUnavailabilities(newUnavailabilities);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données de charge de travail:', error);
        setSaveMessage({
          type: 'error',
          text: 'Échec du chargement des données de charge de travail',
        });
      }
    };
    fetchWorkloadData();
  }, [slot?.id, getAuthHeaders]);

  const totalWorkingDays = slot?.start_date && slot?.end_date ? getWorkingDays(slot.start_date, slot.end_date) : 0;


  const getEmployeeAvailableDays = (employeeId) => {
    return totalWorkingDays - (unavailabilities[employeeId] || 0);
  };

  const wasEmployeePresentDuringSlot = (employee) => {
    if (!slot || !slot.start_date || !slot.end_date || !employee || !employee.arrival_date) return false;
    const employeeArrivalDate = new Date(employee.arrival_date);
    const employeeDepartureDate = employee.departure_date ? new Date(employee.departure_date) : null;
    const slotStartDate = new Date(slot.start_date);
    const slotEndDate = new Date(slot.end_date);
    const arrivedBeforeSlotEnd = employeeArrivalDate <= slotEndDate;
    const presentDuringSlot = !employeeDepartureDate || employeeDepartureDate >= slotStartDate;
    return arrivedBeforeSlotEnd && presentDuringSlot;
  };

  const getEmployeeWorkload = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (!employee || !wasEmployeePresentDuringSlot(employee)) {
      return { allocated: 0, available: 0, percentage: 0 };
    }
    const available = getEmployeeAvailableDays(employeeId);
    const totalSprintDays = sprints.reduce((sum, sprint) => {
      const sprintDays = getWorkingDaysDates(new Date(sprint.start_date), new Date(sprint.end_date)).length;
      return sum + sprintDays;
    }, 0);
    const allocated = sprints.reduce((sum, sprint) => {
      const sprintDays = getWorkingDaysDates(new Date(sprint.start_date), new Date(sprint.end_date)).length;
      const allocation = totalSprintDays > 0 ? (sprintDays / totalSprintDays) * available : 0;
      return sum + allocation;
    }, 0);
    return {
      allocated: parseFloat(allocated.toFixed(1)),
      available: parseFloat(available.toFixed(1)),
      percentage: available > 0 ? (allocated / available) * 100 : 0,
    };
  };

  const saveWorkloadData = useCallback(async () => {
    if (!slot?.id) return;
    setIsSaving(true);
    try {
      const totalSprintDays = sprints.reduce((sum, sprint) => {
        const sprintDays = getWorkingDaysDates(new Date(sprint.start_date), new Date(sprint.end_date)).length;
        return sum + sprintDays;
      }, 0);
      const sprintAllocations = employees.flatMap(employee => {
        const available = totalWorkingDays - (unavailabilities[employee.id] || 0);
        return sprints.map(sprint => {
          const sprintDays = getWorkingDaysDates(new Date(sprint.start_date), new Date(sprint.end_date)).length;
          const allocatedDays = totalSprintDays > 0 ? (sprintDays / totalSprintDays) * available : 0;
          return { employee_id: employee.id, sprint_id: sprint.id, allocated_days: Number(allocatedDays.toFixed(1)) };
        });
      });
      const workloadData = {
        velocity: Number(velocity),
        employee_unavailabilities: Object.entries(unavailabilities)
          .filter(([_, days]) => days > 0)
          .map(([employeeId, days]) => ({ employee_id: Number(employeeId), unavailable_days: Number(days) })),
        sprint_allocations: sprintAllocations
      };
      const response = await fetch(`${apiBaseUrl}/slots/${slot.id}/workload`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(workloadData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Échec de l'enregistrement des données de charge de travail");
      }
      await response.json();
      setSaveMessage({ type: 'success', text: 'Modifications enregistrées avec succès' });
    } catch (err) {
      console.error('Erreur lors de la sauvegarde des données:', err);
      setSaveMessage({ type: 'error', text: "Échec de l'enregistrement des modifications" });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  }, [slot?.id, sprints, employees, totalWorkingDays, unavailabilities, velocity, getAuthHeaders]);

  useEffect(() => {
    if (!slot?.id || !needsSave) return;
    const timeoutId = setTimeout(() => {
      saveWorkloadData();
      setNeedsSave(false);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [needsSave, slot?.id, velocity, unavailabilities, saveWorkloadData]);

  const calculateTeamCapacity = () => {
    let totalCapacity = 0;
    employees.forEach((employee) => {
      if (wasEmployeePresentDuringSlot(employee)) {
        const availableDays = getEmployeeAvailableDays(employee.id);
        totalCapacity += velocity * availableDays;
      }
    });
    return totalCapacity;
  };

  const handleUnavailabilityChange = (employeeId, days) => {
    const numericDays = Number(days) || 0;
    setUnavailabilities((prev) => ({ ...prev, [employeeId]: numericDays }));
    setNeedsSave(true);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium flex items-center mb-4">
          <Settings className="h-5 w-5 mr-2" />
          Configuration de la Vélocité du Projet
        </h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-4 mb-4">
            <label className="text-sm text-gray-600">Tokens par personne par jour :</label>
            <div className="relative flex items-center">
              <input
                type="number"
                value={velocity}
                onChange={(e) => {
                  const newValue = Math.max(0, parseFloat(e.target.value) || 0);
                  setVelocity(newValue);
                  setNeedsSave(true);
                }}
                onBlur={() => setNeedsSave(true)}
                className="border rounded p-2 w-24 pr-12" min="0" step="0.1"
              />
              <span className="absolute right-3 text-gray-500 text-sm">/ jour</span>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Capacité de l'équipe pour ce slot</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-blue-600">Jours ouvrés dans le slot :</div>
                <div className="text-lg font-medium">{totalWorkingDays} jours</div>
              </div>
              <div>
                <div className="text-sm text-blue-600">Vélocité journalière équipe :</div>
                <div className="text-lg font-medium">{(velocity * employees.filter(wasEmployeePresentDuringSlot).length).toFixed(1)} tokens/jour</div>
              </div>
              <div>
                <div className="text-sm text-blue-600">Capacité totale de tokens :</div>
                <div className="text-lg font-medium">{calculateTeamCapacity().toFixed(1)} tokens</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium flex items-center mb-4">
          <Calendar className="h-5 w-5 mr-2" />
          Disponibilité des Employés
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees
            .filter(employee => wasEmployeePresentDuringSlot(employee))
            .map((employee) => {
              const workload = getEmployeeWorkload(employee.id);
              return (
                <EmployeeCard
                  key={employee.id} employee={employee} workload={workload}
                  unavailabilities={unavailabilities} handleUnavailabilityChange={handleUnavailabilityChange}
                  totalWorkingDays={totalWorkingDays} objectives={objectives} velocity={velocity} sprints={sprints}
                />
              );
            })}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            Allocations des Sprints
          </h3>
          <button onClick={() => setShowProgress(prev => !prev)} className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none">
            {showProgress ? "Masquer l'avancement" : "Afficher l'avancement"}
          </button>
        </div>
        <SprintMatrix
          slot={slot} sprints={sprints}
          employees={employees.filter(employee => wasEmployeePresentDuringSlot(employee))}
          objectives={objectives} velocity={velocity} getEmployeeAvailableDays={getEmployeeAvailableDays}
          showProgress={showProgress}
        />
      </div>

      {saveMessage && (
        <div className={`p-4 rounded ${saveMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {saveMessage.text}
        </div>
      )}
      {isSaving && <div className="text-sm text-gray-500">En cours de sauvegarde...</div>}
    </div>
  );
};

const EmployeeCard = ({ employee, workload, unavailabilities, handleUnavailabilityChange, totalWorkingDays, objectives, velocity, sprints }) => {
  const sprintIdsInSlot = sprints.map(sprint => sprint.id);
  const employeeObjectivesInSlotSprints = objectives.filter(objective =>
    objective.owner_id === employee.id && sprintIdsInSlot.includes(objective.sprint_id)
  );
  const totalTokensForEmployeeInSlot = employeeObjectivesInSlotSprints.reduce((sum, objective) => sum + Number(objective.effort), 0);
  const totalPlannedDaysForEmployeeInSlot = velocity > 0 ? parseFloat((totalTokensForEmployeeInSlot / velocity).toFixed(1)) : 0;
  const percentage = workload.available > 0 ? (totalPlannedDaysForEmployeeInSlot / workload.available) * 100 : 0;
  const isOverloaded = percentage > 100;
  const isInactive = !employee.is_active;

  return (
    <div className={`border rounded-lg p-4 ${isInactive ? 'bg-gray-50' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <div><h4 className={`font-medium ${isInactive ? 'text-gray-500' : ''}`}>{employee.name}</h4></div>
        <div className="text-right">
          <div className={`text-sm ${isOverloaded ? 'text-red-600 font-medium' : isInactive ? 'text-gray-500' : 'text-gray-600'}`}>{Math.round(percentage)}%</div>
          <div className="text-xs text-gray-500">{totalPlannedDaysForEmployeeInSlot} / {workload.available} jours</div>
        </div>
      </div>
      <div className="flex items-center space-x-2 mb-2">
        <input type="number" value={unavailabilities[employee.id] || 0} onChange={(e) => handleUnavailabilityChange(employee.id, parseFloat(e.target.value) || 0)}
          className={`border rounded p-1 w-20 text-sm ${isInactive ? 'bg-gray-100' : ''}`} min="0" step="0.5" disabled={isInactive} />
        <span className="text-xs text-gray-500">jours indispo / {totalWorkingDays}</span>
      </div>
      <div className="relative pt-1">
        <div className={`overflow-hidden h-2 text-xs flex rounded ${isInactive ? 'bg-gray-200' : isOverloaded ? 'bg-red-200' : 'bg-purple-200'}`}>
          <div style={{ width: `${Math.min(100, percentage)}%` }}
            className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${isInactive ? 'bg-gray-400' : isOverloaded ? 'bg-red-500' : 'bg-purple-500'}`} />
        </div>
        {isOverloaded && !isInactive && <div className="text-xs text-red-600 mt-1">⚠️ Surcharge de travail</div>}
      </div>
    </div>
  );
};

const SprintMatrix = ({ slot, sprints, employees, objectives, getEmployeeAvailableDays, velocity, showProgress }) => {
  const navigate = useNavigate();
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, title: '', objectives: [] });

  const calculateTooltipPosition = (e) => {
    const margin = 10; let x = e.clientX + margin; let y = e.clientY + margin;
    const estimatedWidth = 300; const estimatedHeight = 200;
    if (x + estimatedWidth > window.innerWidth) x = e.clientX - estimatedWidth - margin;
    if (y + estimatedHeight > window.innerHeight) y = e.clientY - estimatedHeight - margin;
    return { x, y };
  };
  const handleTooltipLeave = () => setTooltip(prev => ({ ...prev, visible: false }));

  const projectsInSlot = useMemo(() => {
    if (!sprints || sprints.length === 0) return [];
    const projectsMap = sprints.reduce((acc, sprint) => {
      const projectId = sprint.project_id;
      if (!acc[projectId]) {
        acc[projectId] = { id: projectId, name: sprint.project_name || `Projet ${projectId}`, sprints: [] };
      }
      acc[projectId].sprints.push(sprint);
      return acc;
    }, {});
    return Object.values(projectsMap).sort((a, b) => a.name.localeCompare(b.name));
  }, [sprints]);

  const handleEmployeeTooltipEnter = (employee, e) => {
    const employeeObjectivesInSlot = objectives.filter(t => t.owner_id === employee.id && sprints.some(s => s.id === t.sprint_id));
    const { x, y } = calculateTooltipPosition(e);
    setTooltip({ visible: true, x, y, title: employee.name, objectives: employeeObjectivesInSlot });
  };

  const handleProjectHeaderTooltipEnter = (project, e) => {
    const projectSprintIds = project.sprints.map(s => s.id);
    const objectivesInProjectSprints = objectives.filter(t => projectSprintIds.includes(t.sprint_id));
    const { x, y } = calculateTooltipPosition(e);
    setTooltip({ visible: true, x, y, title: project.name, objectives: objectivesInProjectSprints });
  };

  const formatAllocation = (val) => (val === 0 ? "" : val.toFixed(1));
  const formatProgress = (val) => (val === 0 ? "0" : val.toFixed(1));

  const handleCellTooltipEnter = (employee, project, e) => {
    const projectSprintIds = project.sprints.map(s => s.id);
    const cellObjectives = objectives.filter(t => t.owner_id === employee.id && projectSprintIds.includes(t.sprint_id));
    const { x, y } = calculateTooltipPosition(e);
    setTooltip({ visible: true, x, y, title: `${employee.name} – ${project.name}`, objectives: cellObjectives });
  };

  return (
    <div className="relative overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 table-fixed">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="sticky left-0 bg-gray-50 z-10 py-3 pl-4 pr-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-40">Employés</th>
            {projectsInSlot.map((project) => (
              <th key={project.id} scope="col" 
                  // MODIFICATION: Ajout de 'normal-case' pour annuler l'uppercase du parent et text-gray-900 pour le style
                  className="px-2 py-3 text-center align-middle text-xs font-medium text-gray-500 tracking-wider w-32 normal-case"> 
                <div className="text-sm font-medium text-gray-900 break-words whitespace-normal max-w-[15ch] cursor-pointer hover:text-blue-600 hover:underline inline-block"
                  onMouseEnter={(e) => handleProjectHeaderTooltipEnter(project, e)} onMouseLeave={handleTooltipLeave}
                  onClick={() => { const params = new URLSearchParams(); params.set('project', project.id); params.set('owner', 'all'); localStorage.setItem("objectiveBoard_viewMode", "project"); navigate(`/objectives?${params.toString()}`); }}
                  title={project.name} >
                  {project.name}
                </div>
              </th>
            ))}
            <th scope="col" 
                // MODIFICATION: Ajout de text-right pour aligner avec les données
                className="sticky right-0 bg-gray-50 z-10 px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-32">
              Total
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {employees.map((employee) => {
            const availableDays = getEmployeeAvailableDays(employee.id);
            let totalEmployeeAllocation = 0;
            let totalEmployeeRealized = 0;
            return (
              <tr key={employee.id} className={employee.is_active ? "" : "bg-gray-50"}>
                <td className="sticky left-0 bg-white z-10 py-2 pl-4 pr-3 text-sm whitespace-nowrap w-40">
                   <div className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 hover:underline"
                    onMouseEnter={(e) => handleEmployeeTooltipEnter(employee, e)} onMouseLeave={handleTooltipLeave}
                    onClick={() => { const params = new URLSearchParams(); params.set('owner', employee.id); params.set('project', 'all'); localStorage.setItem("objectiveBoard_viewMode", "project"); navigate(`/objectives?${params.toString()}`); }}>
                    {employee.name}
                  </div>
                </td>
                {projectsInSlot.map((project) => {
                  let projectComputedAllocation = 0;
                  let projectRealizedAllocation = 0;
                  project.sprints.forEach(sprint => {
                    const sprintObjectivesForEmployee = objectives.filter(objective => objective.owner_id === employee.id && objective.sprint_id === sprint.id);
                    const tokenSumForSprint = sprintObjectivesForEmployee.reduce((sum, objective) => sum + Number(objective.effort), 0);
                    projectComputedAllocation += (velocity > 0 ? tokenSumForSprint / velocity : 0);
                    if (showProgress) {
                      const realizedTokenSumForSprint = sprintObjectivesForEmployee.filter(objective => objective.status === "done").reduce((sum, objective) => sum + Number(objective.effort), 0);
                      projectRealizedAllocation += (velocity > 0 ? realizedTokenSumForSprint / velocity : 0);
                    }
                  });
                  totalEmployeeAllocation += projectComputedAllocation;
                  if (showProgress) totalEmployeeRealized += projectRealizedAllocation;
                  return (
                    <td key={project.id} className="px-2 py-2 whitespace-nowrap text-center w-32">
                        <span onMouseEnter={(e) => handleCellTooltipEnter(employee, project, e)} onMouseLeave={handleTooltipLeave}
                          onClick={() => { if (projectComputedAllocation > 0) { const params = new URLSearchParams(); params.set('project', project.id); params.set('owner', employee.id); localStorage.setItem("objectiveBoard_viewMode", "project"); navigate(`/objectives?${params.toString()}`); } }}
                          className={`text-sm ${projectComputedAllocation > 0 ? "cursor-pointer hover:text-blue-600 hover:underline" : "text-gray-400"}`}>
                          {showProgress
                            ? (projectComputedAllocation === 0 ? "" : `${projectRealizedAllocation.toFixed(1)} / ${projectComputedAllocation.toFixed(1)} j`)
                            : (projectComputedAllocation === 0 ? "" : `${projectComputedAllocation.toFixed(1)} j`)}
                        </span>
                    </td>
                  );
                })}
                <td className={`sticky right-0 bg-white z-10 px-3 py-2 text-sm text-right whitespace-nowrap font-medium w-32 ${!showProgress && totalEmployeeAllocation > availableDays ? "text-red-500" : "text-gray-900"}`}>
                   {showProgress
                    ? totalEmployeeAllocation === 0 ? "" : `${totalEmployeeRealized.toFixed(1)} / ${totalEmployeeAllocation.toFixed(1)} j`
                    : totalEmployeeAllocation === 0 ? "" : `${totalEmployeeAllocation.toFixed(1)} / ${availableDays} j`}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="bg-gray-50">
             <tr>
                {/* MODIFICATION: Changement de th à td pour alignement et suppression de text-right */}
                <td className="sticky left-0 bg-gray-50 z-10 py-3 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap w-40">
                  Total
                </td>
                {projectsInSlot.map((project) => {
                  let totalProjectAlloc = 0;
                  let totalProjectRealized = 0;
                  project.sprints.forEach(sprint => {
                    employees.forEach(employee => {
                        const sprintObjectives = objectives.filter(objective => objective.owner_id === employee.id && objective.sprint_id === sprint.id);
                        const tokenSum = sprintObjectives.reduce((s, objective) => s + Number(objective.effort), 0);
                        totalProjectAlloc += (velocity > 0 ? tokenSum / velocity : 0);
                        if (showProgress) {
                            const realizedTokenSum = sprintObjectives.filter(objective => objective.status === "done").reduce((s, objective) => s + Number(objective.effort), 0);
                            totalProjectRealized += (velocity > 0 ? realizedTokenSum / velocity : 0);
                        }
                    });
                  });
                  return (
                    <td key={project.id} className="px-2 py-2 whitespace-nowrap text-center w-32">
                      <div className="text-sm font-medium">
                        {showProgress
                          ? (totalProjectAlloc === 0 ? "" : `${formatProgress(totalProjectRealized)} / ${formatProgress(totalProjectAlloc)} j`)
                          : (totalProjectAlloc === 0 ? "" : `${formatAllocation(totalProjectAlloc)} j`)}
                      </div>
                    </td>
                  );
                })}
                {/* MODIFICATION: text-right pour aligner le contenu du td comme les données, et non comme un th */}
                <td className="sticky right-0 bg-gray-50 z-10 w-32 px-3 py-2 text-sm text-right whitespace-nowrap font-medium"></td>
          </tr>
        </tfoot>
      </table>
       {tooltip.visible && (
        <div className="fixed bg-white border border-gray-300 shadow-lg p-2 rounded text-sm z-50 tooltip-container" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="font-semibold mb-1">{tooltip.title}</div>
          <ul className="list-none">
            {tooltip.objectives.length > 0 ? (
              tooltip.objectives.map(objective => (
                <li key={objective.id} className="flex items-center mb-1">
                  <span className="mr-2 inline-flex items-center justify-center">
                    {objective.status === "done" ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    : objective.status === "in_progress" ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
                    : objective.status === "blocked" ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                    : objective.status === "backlog" ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor"><path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" /></svg>
                    : objective.status === "ready" ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" /></svg>
                    : <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" /></svg>}
                  </span>
                  <span>{objective.name} <span className="text-gray-500">({(velocity > 0 ? Number(objective.effort) / velocity : 0).toFixed(1)}j)</span></span>
                </li>
              ))
            ) : (<li>Aucun objectif</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WorkloadManagement;
// --- END OF FILE WorkloadManagement.js ---
